<script>
import Layout from "@/router/layouts/main";
import { api } from "@/state/services";

export default {
  components: {
    Layout,
  },
  data() {
    return {
      ebook: null,
      loading: true,
      buying: false,
      downloading: false,
      downloadMessage: "",
      message: "",
    };
  },
  methods: {
    load() {
      const ref = this.$route.params.slug;
      this.loading = true;
      api.get("ebooks/" + encodeURIComponent(ref)).then((response) => {
        this.loading = false;
        if (response.data.status === "success" && response.data.ebook) {
          this.ebook = response.data.ebook;
        } else {
          this.ebook = null;
        }
      }).catch(() => {
        this.loading = false;
        this.ebook = null;
      });
    },
    buy() {
      if (!this.ebook) return;
      this.buying = true;
      this.message = "";
      api
        .post("ebooks/orders", {
          id: this.ebook.id
        })
        .then((response) => {
          this.buying = false;
          if (response.data.status === "success" && response.data.order) {
            this.$router.push("/ebooks/order/" + response.data.order.id);
          } else {
            this.message =
              (response.data && response.data.message) ||
              "Não foi possível iniciar a compra.";
          }
        })
        .catch(() => {
          this.buying = false;
          this.message = "Erro ao processar.";
        });
    },
    downloadPdf() {
      if (!this.ebook || !this.ebook.owned) return;
      this.downloading = true;
      this.downloadMessage = "";
      const q = "?format=" + encodeURIComponent("pdf");
      api
        .get("ebooks/" + encodeURIComponent(this.ebook.id) + "/download" + q)
        .then((response) => {
          this.downloading = false;
          if (response.data.status === "success" && response.data.url) {
            const name =
              (response.data.filename && String(response.data.filename)) ||
              this.suggestedEbookPdfFilename();
            const link = document.createElement("a");
            link.href = response.data.url;
            link.setAttribute("download", name);
            link.rel = "noopener noreferrer";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            this.downloadMessage =
              (response.data && response.data.message) ||
              "Não foi possível gerar o link.";
          }
        })
        .catch(() => {
          this.downloading = false;
          this.downloadMessage = "Erro ao baixar.";
        });
    },
    suggestedEbookPdfFilename() {
      if (!this.ebook || !this.ebook.title) {
        return "ebook.pdf";
      }
      const base = String(this.ebook.title)
        .replace(/[\r\n"<>|*?/\\]+/g, "_")
        .replace(/^\.+$/, "ebook")
        .trim()
        .slice(0, 100);
      const withExt = /\.pdf$/i.test(base) ? base : base + ".pdf";
      return withExt || "ebook.pdf";
    },
  },
  mounted() {
    this.load();
  },
  watch: {
    "$route.params.slug"() {
      this.load();
    },
  },
};
</script>

<template>
  <Layout>
    <div class="ebook-page">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link tag="a" to="/">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="fill: rgba(0, 0, 0, 1);">
              <path d="M5 22h14a2 2 0 0 0 2-2v-9a1 1 0 0 0-.29-.71l-8-8a1 1 0 0 0-1.41 0l-8 8A1 1 0 0 0 3 11v9a2 2 0 0 0 2 2zm5-2v-5h4v5zm-5-8.59 7-7 7 7V20h-3v-5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v5H5z"></path>
            </svg>
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link tag="a" to="/ebooks">E-books</router-link>
        </li>
        <li class="breadcrumb-item">{{ ebook ? ebook.title : "..." }}</li>
      </ol>

      <div v-if="loading" class="card ebook-state-card">
        <div class="card-body min-vh-25 d-flex justify-content-center align-items-center">
          <b-spinner variant="default"></b-spinner>
        </div>
      </div>

      <div v-else-if="!ebook" class="card ebook-state-card">
        <div class="card-body text-center py-5">
          <p class="mb-0 text-muted">O e-book não foi encontrado.</p>
          <router-link class="btn btn-sm btn-outline-primary mt-3" to="/ebooks">Voltar ao catálogo</router-link>
        </div>
      </div>

      <div v-else class="card ebook-detail-card">
        <div class="card-body p-0">
          <div class="row no-gutters ebook-detail-inner">
            <div class="col-lg-5 ebook-cover-column">
              <div class="ebook-cover-shell">
                <img
                  v-if="ebook.cover_url"
                  class="ebook-cover-img"
                  :src="ebook.cover_url"
                  :alt="ebook.title"
                />
                <div v-else class="ebook-cover-placeholder">
                  <span class="ebook-cover-placeholder-text">Sem capa</span>
                </div>
              </div>
            </div>
            <div class="col-lg-7 ebook-detail-column">
              <div class="ebook-detail-body">
                <header class="ebook-detail-header">
                  <h1 class="ebook-detail-title">{{ ebook.title }}</h1>
                  <p v-if="ebook.subtitle" class="ebook-detail-subtitle">{{ ebook.subtitle }}</p>
                  <p v-if="ebook.authors" class="ebook-detail-authors">
                    <span class="ebook-detail-authors-label">Autores</span>
                    {{ ebook.authors }}
                  </p>
                </header>

                <div class="ebook-price-bar">
                  <span class="ebook-price-label">Preço</span>
                  <span class="ebook-price-value">{{ ebook.price | currency }}</span>
                </div>

                <div v-if="ebook.description" class="ebook-section">
                  <h2 class="ebook-section-title">Sobre</h2>
                  <div v-html="ebook.description" class="ebook-desc"></div>
                </div>

                <div v-if="ebook.summary" class="ebook-section ebook-section-summary">
                  <h2 class="ebook-section-title">Sumário</h2>
                  <div v-html="ebook.summary" class="ebook-desc"></div>
                </div>

                <footer class="ebook-detail-actions">
                  <template v-if="ebook.owned">
                    <p v-if="downloadMessage" class="text-danger small w-100 mb-2">
                      {{ downloadMessage }}
                    </p>
                    <button
                      type="button"
                      class="btn btn-default btn-lg btn-block ebook-btn-buy"
                      :disabled="downloading"
                      @click="downloadPdf"
                    >
                      <span v-if="downloading">Gerando link...</span>
                      <span v-else>Baixar PDF</span>
                    </button>
                    <router-link
                      class="btn btn-outline-secondary btn-lg btn-block"
                      to="/ebooks/library"
                    >
                      Abrir biblioteca
                    </router-link>
                  </template>
                  <button
                    v-else
                    type="button"
                    class="btn btn-default btn-lg btn-block ebook-btn-buy"
                    :disabled="buying"
                    @click="buy"
                  >
                    <span v-if="buying">Processando...</span>
                    <span v-else>Comprar</span>
                  </button>
                </footer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.ebook-page {
  --ebook-ink: #1a1512;
  --ebook-muted: #6b6560;
  --ebook-default-tint: #f5f2f8;
  --ebook-default-tint-deep: #ebe4f2;
  --ebook-accent: #452058;
  --ebook-accent-soft: rgba(69, 32, 88, 0.08);
  --ebook-radius: 8px;
  --ebook-card-border: 1px solid rgba(0, 0, 0, 0.125);
}

.ebook-state-card {
  border: var(--ebook-card-border);
  box-shadow: none;
}

.ebook-detail-card {
  border: var(--ebook-card-border);
  box-shadow: none;
  overflow: hidden;
  background: #fff;
}

.ebook-detail-inner {
  min-height: 280px;
}

.ebook-cover-column {
  background: var(--ebook-default-tint);
  padding: 1.75rem 1.5rem 2rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

@media (min-width: 992px) {
  .ebook-cover-column {
    padding: 2.25rem 2rem 2.5rem;
    align-items: center;
    min-height: 100%;
  }
}

.ebook-cover-shell {
  width: 100%;
  max-width: 320px;
  margin: 0 auto;
}

.ebook-cover-img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
}

.ebook-cover-placeholder {
  aspect-ratio: 2 / 3;
  background: var(--ebook-default-tint-deep);
  border: 2px dashed rgba(69, 32, 88, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.ebook-cover-placeholder-text {
  font-size: 0.85rem;
  color: var(--ebook-muted);
}

.ebook-detail-column {
  background: #fff;
}

.ebook-detail-body {
  padding: 1.75rem 1.5rem 2rem;
}

@media (min-width: 992px) {
  .ebook-detail-body {
    padding: 2.25rem 2.25rem 2.5rem 1.5rem;
  }
}

.ebook-detail-header {
  margin-bottom: 1.25rem;
}

.ebook-detail-title {
  font-size: 1.65rem;
  font-weight: 600;
  line-height: 1.25;
  color: var(--ebook-ink);
  letter-spacing: -0.02em;
  margin-bottom: 0.5rem;
}

@media (min-width: 768px) {
  .ebook-detail-title {
    font-size: 1.85rem;
  }
}

.ebook-detail-subtitle {
  font-size: 1rem;
  color: var(--ebook-muted);
  line-height: 1.45;
  margin-bottom: 0.75rem;
}

.ebook-detail-authors {
  font-size: 0.9rem;
  color: var(--ebook-ink);
  margin-bottom: 0;
}

.ebook-detail-authors-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ebook-muted);
  margin-bottom: 0.2rem;
}

.ebook-price-bar {
  padding: 0.85rem 1rem;
  margin-bottom: 1.5rem;
  border-radius: 8px;
  background: var(--ebook-accent-soft);
  border: 1px solid rgba(69, 32, 88, 0.12);
}

.ebook-price-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ebook-accent);
  margin-top: 5px;
}

.ebook-price-value {
  display: block;
  font-family: "Mulish", "Poppins", sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--ebook-accent);
  letter-spacing: -0.02em;
  margin: 0;
}

.ebook-section {
  margin-bottom: 1.5rem;
}

.ebook-section-summary {
  padding: 10px 15px;
  border-left: 3px solid var(--ebook-accent);
}

.ebook-section-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--ebook-muted);
  margin-bottom: 0;
}

.ebook-desc {
  font-size: 0.95rem;
  line-height: 1.65;
  color: var(--ebook-ink);
}

.ebook-desc :deep(p) {
  margin-bottom: 0.65rem;
}

.ebook-desc :deep(p:last-child) {
  margin-bottom: 0;
}

.ebook-detail-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
  padding-top: 0.5rem;
  margin-top: 0.25rem;
}
</style>
